import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputBase, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';

import { ActiveFiltersBar } from '../../../components/molecules/FilterBar/ActiveFiltersBar';
import { AddFilterModal } from '../../../components/molecules/FilterBar/AddFilterModal';
import {
  FilterFieldDef,
  OPS_NUM_SIMPLE,
  OPS_TEXT_FULL,
} from '../../../components/molecules/FilterBar/AddFilterModal.types';
import { AdvancedFilter } from '../../../components/molecules/FilterBar/FilterBar.props';
import { MultiSelectDropdown } from '../../../components/molecules/FilterBar/MultiSelectDropdown';
import { PICK_TASK_STATUS_OPTIONS } from '../pickTaskHelpers';

export interface PickTaskFilters {
  statuses: string[];
  multiSearch: string;
  advancedFilters: AdvancedFilter[];
}

const PICK_TASK_FILTER_FIELDS: FilterFieldDef[] = [
  { name: 'ID', key: 'id', type: 'number', category: 'Details', operators: OPS_NUM_SIMPLE },
  { name: 'Status', key: 'status', type: 'text', category: 'Details', operators: OPS_TEXT_FULL },
  {
    name: 'Allocated To',
    key: 'allocatedToUserName',
    type: 'text',
    category: 'Details',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Source',
    key: 'fromLocationName',
    type: 'text',
    category: 'Details',
    operators: OPS_TEXT_FULL,
  },
  {
    name: 'Destination',
    key: 'toLocationName',
    type: 'text',
    category: 'Details',
    operators: OPS_TEXT_FULL,
  },
];
const PICK_TASK_FILTER_CATEGORIES = ['Details'];

export interface PickTaskFilterBarProps {
  filters: PickTaskFilters;
  onChange: (filters: PickTaskFilters) => void;
  onSearch: () => void;
  onReset: () => void;
}

export const PickTaskFilterBar: React.FC<PickTaskFilterBarProps> = ({
  filters,
  onChange,
  onSearch,
  onReset,
}) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const advanced = filters.advancedFilters ?? [];
  const set = (patch: Partial<PickTaskFilters>) => onChange({ ...filters, ...patch });
  const setAndSearch = (patch: Partial<PickTaskFilters>) => {
    onChange({ ...filters, ...patch });
    onSearch();
  };

  const handleApplyFilter = (filter: AdvancedFilter) => {
    const next =
      editingIndex !== null
        ? advanced.map((f, i) => (i === editingIndex ? filter : f))
        : [...advanced, filter];
    setEditingIndex(null);
    setAndSearch({ advancedFilters: next });
  };
  const handleRemoveFilter = (index: number) =>
    setAndSearch({ advancedFilters: advanced.filter((_, i) => i !== index) });

  const inputSx = {
    border: `1px solid ${theme.palette.v4.filterInputBorder}`,
    borderRadius: 1,
    px: 1,
    py: 0.25,
    fontSize: '0.75rem',
    bgcolor: theme.palette.v4.surface,
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          p: 1.25,
          bgcolor: theme.palette.v4.surface,
          border: `1px solid ${theme.palette.v4.border}`,
          borderRadius: 1,
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSearch();
        }}
      >
        <MultiSelectDropdown
          label="Status"
          options={PICK_TASK_STATUS_OPTIONS}
          selected={filters.statuses}
          onChange={(vals) => set({ statuses: vals })}
        />

        <Button
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingIndex(null);
            setModalOpen(true);
          }}
          aria-label="Add advanced filter"
          sx={{
            textTransform: 'none',
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.v4.filterInputBorder}`,
            borderRadius: 1,
            px: 1,
            py: 0.25,
          }}
        >
          <Typography variant="body2">Add Filter</Typography>
        </Button>

        <Button
          onClick={onReset}
          aria-label="Clear all filters"
          sx={{
            textTransform: 'none',
            color: theme.palette.error.main,
            border: `1px solid ${theme.palette.v4.filterInputBorder}`,
            borderRadius: 1,
            px: 1,
            py: 0.25,
          }}
        >
          <Typography variant="body2">Clear All</Typography>
        </Button>

        <InputBase
          placeholder="Search all fields..."
          value={filters.multiSearch}
          onChange={(e) => set({ multiSearch: e.target.value })}
          inputProps={{ 'aria-label': 'Search all fields' }}
          sx={{ ...inputSx, width: 240, ml: 'auto' }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
          onClick={onSearch}
          aria-label="Search pick tasks"
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            bgcolor: theme.palette.v4.accent,
            px: 3,
            py: 0.25,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          Search
        </Button>
      </Box>

      <ActiveFiltersBar
        filters={advanced}
        onRemove={handleRemoveFilter}
        onEdit={(index) => {
          setEditingIndex(index);
          setModalOpen(true);
        }}
      />

      <AddFilterModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingIndex(null);
        }}
        onApply={handleApplyFilter}
        recentlyUsed={[]}
        fieldDefinitions={PICK_TASK_FILTER_FIELDS}
        categories={PICK_TASK_FILTER_CATEGORIES}
        editingFilter={editingIndex !== null ? advanced[editingIndex] : undefined}
      />
    </Box>
  );
};
